import React from 'react';

/**
 * SOPrint Component - Production-focused Sales Order Print Layout
 * 
 * Features:
 * - SO Line Item Numbers (format: {SO_NUMBER}-{LINE_NUMBER})
 * - Target Production Date (delivery date - 3 days)
 * - Item header with Type (MAKE/BUY) and Status
 * - Production Summary with signature lines
 * - Optimized for black & white printing
 */

// Helper function to generate SO line item number
// Fallback for old SOs without stored item_code
const generateSOLineNumber = (soNumber, index) => {
  return `${soNumber}-${String(index + 1).padStart(2, '0')}`;
};

// Get SO line number - prefer stored item_code, fallback to index-based generation
const getSOLineNumber = (lineItem, soNumber, index) => {
  // Use stored item_code if it exists and follows the SO pattern (e.g., TREUD0050A-01)
  if (lineItem.item_code && lineItem.item_code.startsWith(soNumber + '-')) {
    return lineItem.item_code;
  }
  // Fallback to index-based generation for old SOs or corrupted data
  return generateSOLineNumber(soNumber, index);
};

// Helper function to determine item type (MAKE/BUY)
const getItemType = (lineItem, freshItem) => {
  // Check fulfillment_type first, then source_type
  if (lineItem.fulfillment_type) {
    return lineItem.fulfillment_type.toUpperCase();
  }
  const sourceType = freshItem?.source_type || lineItem.item_source_type || 'MP';
  return sourceType === 'IP' ? 'MAKE' : 'BUY';
};

// Helper function to calculate item status
const calculateItemStatus = (lineItem) => {
  // Check if BOM is missing/pending
  if (lineItem.has_bom === false) {
    return 'BOM Pending';
  }
  // Check if variants need selection
  if (lineItem.needs_variant_selection) {
    return 'Variants Pending';
  }
  // Otherwise production ready
  return 'Production Ready';
};

// Helper function to calculate target production date (delivery - 3 days)
// Used ONLY as fallback for legacy orders without target_production_date
const calculateTargetProductionDate = (deliveryDate) => {
  if (!deliveryDate) return null;
  const delivery = new Date(deliveryDate);
  const target = new Date(delivery);
  target.setDate(target.getDate() - 3);
  return target;
};

// Helper function to format date as DD/MM/YYYY
const formatDateDMY = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '-';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const SOPrint = React.forwardRef(({ order, clients, items }, ref) => {
  if (!order) return null;

  // Get client data
  const client = clients?.find(c => c.id === order.customer_id);
  
  // Get city name
  let cityName = '';
  if (order.delivery_address_id && order.delivery_address_id !== 'default' && client) {
    const addr = client.delivery_addresses?.find(a => (a.id || a.city_code) === order.delivery_address_id);
    cityName = addr?.city || '';
  }
  if (!cityName && client) {
    cityName = client.city || client.delivery_addresses?.[0]?.city || '';
  }

  // Use stored target_production_date, fallback to calculation for legacy orders
  const targetProductionDate = order.target_production_date 
    ? new Date(order.target_production_date)
    : calculateTargetProductionDate(order.delivery_date);

  // Enrich line items with calculated fields
  const enrichedLineItems = order.line_items?.map((lineItem, index) => {
    const freshItem = items?.find(i => i.id === lineItem.item_id);
    return {
      ...lineItem,
      item_name: freshItem?.name || lineItem.item_name,
      item_image: freshItem?.image_url || lineItem.item_image,
      so_line_number: getSOLineNumber(lineItem, order.so_number, index),
      make_or_buy: getItemType(lineItem, freshItem),
      item_status: calculateItemStatus(lineItem)
    };
  }) || [];

  // Calculate summary stats
  const totalItems = enrichedLineItems.length;
  const totalUnits = enrichedLineItems.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);
  const makeItems = enrichedLineItems.filter(item => item.make_or_buy === 'MAKE').length;
  const buyItems = enrichedLineItems.filter(item => item.make_or_buy === 'BUY').length;

  return (
    <div ref={ref} style={styles.container}>
      {/* HEADER SECTION */}
      <div style={styles.header}>
        <div style={styles.headerTitle}>SALES ORDER</div>
      </div>

      {/* CLIENT AND ORDER DETAILS */}
      <div style={styles.infoGrid}>
        <div style={styles.infoBox}>
          <div style={styles.sectionHeader}>CLIENT DETAILS</div>
          <div style={styles.clientName}>{order.customer_name || client?.name}</div>
          {cityName && <div style={styles.infoText}>City: {cityName}</div>}
        </div>
        <div style={styles.infoBox}>
          <div style={styles.sectionHeader}>ORDER DETAILS</div>
          <div style={styles.soNumber}>SO Number: {order.so_number}</div>
          <div style={styles.infoText}>Order Date: {formatDateDMY(order.created_at || order.order_date)}</div>
          <div style={styles.targetDate}>Target Production Date: {formatDateDMY(targetProductionDate)}</div>
        </div>
      </div>

      {/* LINE ITEMS */}
      <div style={styles.itemsSection}>
        {enrichedLineItems.map((item, idx) => (
          <div key={idx} style={styles.itemCard}>
            {/* Item Content - Image with info below, Details on right */}
            <div style={styles.itemContent}>
              {/* Left Column: Image + SO Item Info */}
              <div style={styles.leftColumn}>
                {item.item_image ? (
                  <img 
                    src={item.item_image} 
                    alt={item.item_name} 
                    style={styles.itemImage}
                  />
                ) : (
                  <div style={styles.noImage}>No Image</div>
                )}
                {/* SO Item Info Below Image */}
                <div style={styles.itemInfoBox}>
                  <div style={styles.itemInfoLine}><span style={styles.infoLabel}>SO Item:</span> <span style={styles.infoValue}>{item.so_line_number}</span></div>
                  <div style={styles.itemInfoLine}><span style={styles.infoLabel}>Type:</span> <span style={styles.infoValue}>{item.make_or_buy}</span></div>
                  <div style={styles.itemInfoLine}><span style={styles.infoLabel}>Status:</span> {item.item_status}</div>
                </div>
              </div>
              
              {/* Right Column: Details */}
              <div style={styles.itemDetails}>
                <div style={styles.itemName}>{item.item_name}</div>
                <div style={styles.itemQuantity}>
                  <span style={styles.label}>Quantity:</span> {item.quantity} {item.unit || 'units'}
                </div>
                
                {/* Variants */}
                {item.variant_selections?.length > 0 && (
                  <div style={styles.specsSection}>
                    <span style={styles.smallLabel}>Variants: </span>
                    {item.variant_selections.map((vs, i) => (
                      <span key={i}>{vs.variant_group_name}: {vs.selected_item_name}{i < item.variant_selections.length - 1 ? ', ' : ''}</span>
                    ))}
                  </div>
                )}
                
                {/* Sizes */}
                {item.sizes && (
                  <div style={styles.specsSection}>
                    <span style={styles.smallLabel}>Sizes:</span> {item.sizes}
                  </div>
                )}
                
                {/* Specifications */}
                {item.specs && (
                  <div style={styles.specsSection}>
                    <span style={styles.smallLabel}>Specifications:</span>
                    <ul style={styles.bulletList}>
                      {typeof item.specs === 'object' 
                        ? Object.entries(item.specs).map(([k, v], i) => (
                            <li key={i}>{k}: {v}</li>
                          ))
                        : item.specs.split('\n').filter(s => s.trim()).map((spec, i) => (
                            <li key={i}>{spec}</li>
                          ))
                      }
                    </ul>
                  </div>
                )}
                
                {/* Remarks */}
                {item.remarks && (
                  <div style={styles.remarksBox}>
                    <span style={styles.smallLabel}>Remarks:</span> {
                      typeof item.remarks === 'object' 
                        ? JSON.stringify(item.remarks) 
                        : item.remarks
                    }
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* PRODUCTION SUMMARY */}
      <div style={styles.summarySection}>
        <div style={styles.sectionHeader}>PRODUCTION SUMMARY</div>
        <div style={styles.summaryGrid}>
          <div style={styles.summaryItem}>
            <span style={styles.label}>Total Line Items:</span> {totalItems}
          </div>
          <div style={styles.summaryItem}>
            <span style={styles.label}>Total Units:</span> {totalUnits}
          </div>
          <div style={styles.summaryItem}>
            <span style={styles.label}>Items to Manufacture (MAKE):</span> {makeItems}
          </div>
          <div style={styles.summaryItem}>
            <span style={styles.label}>Items to Purchase (BUY):</span> {buyItems}
          </div>
        </div>
        
        <div style={styles.targetProductionBox}>
          <span style={styles.label}>Target Production Completion:</span> {formatDateDMY(targetProductionDate)}
        </div>
        
        {/* Signature Lines */}
        <div style={styles.signatureSection}>
          <div style={styles.signatureLine}>
            <div style={styles.signatureLabel}>Prepared By:</div>
            <div style={styles.signatureUnderline}></div>
            <div style={styles.signatureDate}>Date: _______________</div>
          </div>
          <div style={styles.signatureLine}>
            <div style={styles.signatureLabel}>Production Manager:</div>
            <div style={styles.signatureUnderline}></div>
            <div style={styles.signatureDate}>Date: _______________</div>
          </div>
        </div>
      </div>
    </div>
  );
});

SOPrint.displayName = 'SOPrint';

// Styles object - COMPACT layout optimized for black & white printing
const styles = {
  container: {
    fontFamily: 'Arial, sans-serif',
    fontSize: '10px',
    lineHeight: '1.3',
    color: '#000',
    backgroundColor: '#fff',
    padding: '8px',
    maxWidth: '210mm', // A4 width
  },
  
  // Header styles - COMPACT
  header: {
    textAlign: 'center',
    borderTop: '2px solid #000',
    borderBottom: '2px solid #000',
    padding: '3px 0',
    marginBottom: '8px',
  },
  headerTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    margin: '0',
  },
  
  // Info grid styles - COMPACT
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px',
    marginBottom: '8px',
  },
  infoBox: {
    border: '1px solid #333',
    padding: '6px 8px',
  },
  sectionHeader: {
    fontSize: '10px',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    marginBottom: '4px',
    borderBottom: '1px solid #333',
    paddingBottom: '2px',
  },
  clientName: {
    fontWeight: 'bold',
    fontSize: '11px',
    marginBottom: '2px',
  },
  infoText: {
    margin: '2px 0',
    fontSize: '10px',
  },
  soNumber: {
    fontWeight: 'bold',
    fontSize: '11px',
    margin: '2px 0',
  },
  targetDate: {
    fontWeight: 'bold',
    margin: '2px 0',
    fontSize: '10px',
  },
  
  // Items section styles - COMPACT
  itemsSection: {
    marginBottom: '8px',
  },
  itemCard: {
    border: '1px solid #000',
    marginBottom: '6px',
    pageBreakInside: 'avoid',
  },
  itemContent: {
    display: 'flex',
    padding: '6px',
    gap: '10px',
  },
  // Left column with image and item info - 30% width
  leftColumn: {
    flexShrink: 0,
    width: '30%',
    maxWidth: '180px',
  },
  itemImage: {
    width: '100%',
    height: '120px',
    objectFit: 'cover',
    border: '1px solid #ddd',
  },
  noImage: {
    width: '100%',
    height: '120px',
    backgroundColor: '#f0f0f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#999',
    fontSize: '9px',
    border: '1px solid #ddd',
  },
  itemInfoBox: {
    backgroundColor: '#f5f5f5',
    border: '1px solid #ccc',
    padding: '4px 6px',
    marginTop: '4px',
    fontSize: '9px',
    whiteSpace: 'nowrap',
  },
  itemInfoLine: {
    margin: '1px 0',
    lineHeight: '1.3',
  },
  infoLabel: {
    fontWeight: 'bold',
  },
  infoValue: {
    fontWeight: 'bold',
    fontSize: '10px',
  },
  itemDetails: {
    flex: 1,
  },
  itemName: {
    fontWeight: 'bold',
    fontSize: '11px',
    marginBottom: '3px',
    lineHeight: '1.3',
  },
  itemQuantity: {
    marginBottom: '4px',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  label: {
    fontWeight: 'bold',
  },
  smallLabel: {
    fontWeight: 'bold',
    fontSize: '9px',
    color: '#333',
  },
  specsSection: {
    marginBottom: '3px',
    fontSize: '9px',
    lineHeight: '1.2',
  },
  bulletList: {
    margin: '2px 0 0 15px',
    padding: 0,
    fontSize: '9px',
    lineHeight: '1.3',
  },
  remarksBox: {
    backgroundColor: '#f9f9f9',
    padding: '3px 6px',
    borderLeft: '2px solid #666',
    fontSize: '9px',
    marginTop: '4px',
  },
  
  // Summary section styles - COMPACT
  summarySection: {
    border: '1px solid #000',
    padding: '6px',
    marginTop: '8px',
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '4px',
    marginBottom: '6px',
    fontSize: '10px',
  },
  summaryItem: {
    padding: '2px 0',
  },
  targetProductionBox: {
    backgroundColor: '#f0f0f0',
    padding: '4px 8px',
    fontWeight: 'bold',
    fontSize: '10px',
    textAlign: 'center',
    marginBottom: '8px',
    border: '1px solid #333',
  },
  signatureSection: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '30px',
    marginTop: '12px',
    paddingTop: '8px',
    borderTop: '1px solid #ccc',
  },
  signatureLine: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  signatureLabel: {
    fontWeight: 'bold',
    fontSize: '9px',
  },
  signatureUnderline: {
    borderBottom: '1px solid #000',
    height: '18px',
  },
  signatureDate: {
    fontSize: '9px',
    color: '#666',
  },
};

export default SOPrint;
